import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Baby, Smile, User, Heart,
  ChevronLeft, ChevronRight,
  Gift, Globe, Check, ImageIcon,
  Loader2, Users, Camera, CreditCard, Ticket
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const profiles = [
  { value: 'infantil', label: 'profiles.child', icon: Baby, color: 'bg-yellow-100 text-yellow-600 border-yellow-200', disabled: true },
  { value: 'adolescente', label: 'profiles.teen', icon: Smile, color: 'bg-orange-100 text-orange-600 border-orange-200', disabled: true },
  { value: 'adulto', label: 'profiles.adult', icon: User, color: 'bg-blue-100 text-blue-600 border-blue-200', disabled: false },
  { value: 'tercera_edad', label: 'profiles.senior', icon: Heart, color: 'bg-pink-100 text-pink-600 border-pink-200', disabled: true },
];

const occasions = [
  { value: 'cumpleanos', key: 'cumpleanos' },
  { value: 'navidad', key: 'navidad' },
  { value: 'jubilacion', key: 'jubilacion' },
  { value: 'aniversario', key: 'aniversario' },
  { value: 'graduacion', key: 'graduacion' },
  { value: 'dia_madre', key: 'dia_madre' },
  { value: 'dia_padre', key: 'dia_padre' },
  { value: 'otro', key: 'otro' },
];

const languages = [
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

const CreateBook = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [coverTemplates, setCoverTemplates] = useState([]);
  const [formData, setFormData] = useState({
    profile_type: '',
    recipient_name: '',
    recipient_last_name: '',
    recipient_age: '',
    occasion: '',
    occasion_other: '',
    occasion_detail: '',
    client_relationship: '',
    client_relationship_other: '',
    protagonist_photo: null,
    language: i18n.language,
    cover_template_id: '',
    allow_collaborator_purchase: false,
    allow_collaborator_referrals: false,
  });

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [createdBookId, setCreatedBookId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Additional-book purchase: books beyond the first are paid UPFRONT,
  // before the wizard. Paying grants a credit that POST /books consumes.
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [payingCredit, setPayingCredit] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponState, setCouponState] = useState(null); // null | checking | valid | invalid
  const [couponError, setCouponError] = useState('');

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/books/additional-quote`, { withCredentials: true });
        setPurchaseInfo(res.data);
      } catch {
        // Informative only — the backend enforces payment anyway.
      } finally {
        setQuoteLoading(false);
      }
    };
    fetchQuote();
    if (new URLSearchParams(window.location.search).get('payment') === 'cancelled') {
      toast.message('Pago cancelado. Puedes intentarlo de nuevo cuando quieras.');
    }
  }, []);

  const handlePurchaseCredit = async () => {
    setPayingCredit(true);
    try {
      const res = await axios.post(`${API_URL}/api/books/purchase-credit`, {
        coupon_code: couponState === 'valid' ? couponCode.trim() : null,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (res.data.status === 'credit_granted') {
        toast.success('¡Listo! Tu nuevo libro está pagado. Ahora configúralo.');
        setPurchaseInfo(prev => ({ ...(prev || {}), payment_required: false, book_credits: ((prev?.book_credits) || 0) + 1 }));
        return;
      }
      if (res.data.checkout_url) {
        toast.message('Te llevamos al pago seguro con Stripe…');
        window.location.href = res.data.checkout_url;
        return;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar el pago');
    } finally {
      setPayingCredit(false);
    }
  };

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponState('checking');
    try {
      const res = await axios.get(`${API_URL}/api/books/additional-quote`, {
        params: { coupon_code: code },
        withCredentials: true,
      });
      setPurchaseInfo(res.data);
      if (res.data.coupon_valid) {
        setCouponState('valid');
        setCouponError('');
        toast.success('Cupón aplicado');
      } else {
        setCouponState('invalid');
        setCouponError(res.data.coupon_error || 'Cupón no válido');
      }
    } catch {
      setCouponState('invalid');
      setCouponError('No se pudo validar el cupón');
    }
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/cover-templates`, { withCredentials: true });
        setCoverTemplates(res.data.filter(t => t.is_active));
      } catch {
        // Templates are optional
      }
    };
    fetchTemplates();
  }, []);

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.profile_type !== '';
      case 2:
        if (!formData.recipient_name || !formData.occasion) return false;
        if (formData.occasion === 'otro' && !formData.occasion_other.trim()) return false;
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const occasionValue = formData.occasion === 'otro' ? formData.occasion_other.trim() : formData.occasion;
      
      // 1. Create the book
      const response = await axios.post(`${API_URL}/api/books`, {
        profile: {
          profile_type: formData.profile_type,
          recipient_name: formData.recipient_name,
          recipient_last_name: formData.recipient_last_name || null,
          recipient_age: formData.recipient_age ? parseInt(formData.recipient_age) : null,
          occasion: occasionValue,
          occasion_other: formData.occasion === 'otro' ? formData.occasion_other.trim() : null,
          occasion_detail: formData.occasion_detail.trim() || null,
        },
        language: formData.language,
        cover_template_id: formData.cover_template_id || null,
        allow_collaborator_purchase: formData.allow_collaborator_purchase,
        allow_collaborator_referrals: formData.allow_collaborator_referrals,
        client_relationship: formData.client_relationship || null,
        client_relationship_other: formData.client_relationship === 'otro'
          ? (formData.client_relationship_other || '').trim() || null
          : null,
        protagonist_photo: formData.protagonist_photo || null,
      }, { withCredentials: true });

      const bookId = response.data.book_id;

      toast.success((purchaseInfo?.books_count || 0) >= 1
        ? '¡Libro creado!'
        : '¡Libro creado! El libro base ya está pagado desde tu registro.');
      setCreatedBookId(bookId);
      setShowOnboarding(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('createBook.errorCreating'));
    } finally {
      setLoading(false);
    }
  };

  if (showOnboarding && createdBookId) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto">
            <Card className="card-elevated" data-testid="onboarding-card">
              <CardContent className="pt-8 pb-8 space-y-6 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-bold mb-2">¡Libro creado!</h2>
                  <p className="text-muted-foreground">
                    El libro de <strong>{formData.recipient_name}</strong> esta en marcha.
                  </p>
                </div>
                <div className="text-left bg-muted/30 rounded-xl p-5 space-y-4">
                  <h3 className="font-medium text-sm">Asi es el proceso de creacion:</h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
                      <p><strong className="text-foreground">Rellena los datos del protagonista.</strong> Cuanto mas nos cuentes sobre {formData.recipient_name}, mas personalizado sera el libro.</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
                      <p><strong className="text-foreground">Invita a familiares y amigos.</strong> Podran aportar anecdotas, recuerdos y fotos que enriqueceran el libro.</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">3</span>
                      <p><strong className="text-foreground">La inteligencia artificial escribe el libro.</strong> Con toda la informacion recopilada, generamos un libro unico y personalizado.</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">4</span>
                      <p><strong className="text-foreground">Elige titulo, portada y revisa.</strong> Tendras la oportunidad de revisar el contenido antes de imprimir.</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0 font-bold">5</span>
                      <p><strong className="text-foreground">Impresion y envio.</strong> El libro se imprime en tapa blanda A5 (200 paginas) y se envia en 15-20 dias.</p>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate(`/books/${createdBookId}`)} 
                  className="w-full gap-2 mt-2"
                  data-testid="start-filling-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                  Empezar con los datos del protagonista
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Upfront payment gate: additional books are paid BEFORE the
              wizard. While the quote loads we show a small loader to avoid
              flashing the wizard for users who still have to pay. */}
          {quoteLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="create-book-loading">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Preparando tu nuevo libro…</p>
            </div>
          ) : purchaseInfo?.payment_required ? (
            <Card className="card-elevated" data-testid="additional-book-payment-screen">
              <CardHeader>
                <CardTitle className="font-heading text-2xl flex items-center gap-2.5">
                  <CreditCard className="w-6 h-6 text-primary" />
                  Tu nuevo libro
                </CardTitle>
                <CardDescription>
                  Tu primer libro quedó cubierto con el pago del registro. Para crear un libro
                  adicional, completa primero el pago seguro. Justo después configurarás el libro
                  (protagonista, ocasión, idioma…).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted/40 border rounded-lg p-4 text-sm space-y-1.5" data-testid="additional-book-quote">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Libro personalizado (envío en España incluido)</span>
                    <span>{Number(purchaseInfo?.quote?.base_price_eur ?? 60).toFixed(2)}€</span>
                  </div>
                  {(purchaseInfo?.quote?.discount_eur ?? 0) > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>{purchaseInfo.quote.discount_description || 'Descuento'}</span>
                      <span>-{Number(purchaseInfo.quote.discount_eur).toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>IVA (21%)</span>
                    <span>{Number(purchaseInfo?.quote?.tax_eur ?? 12.6).toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between font-semibold text-base border-t pt-2 mt-1">
                    <span>Total</span>
                    <span data-testid="additional-book-total">{Number(purchaseInfo?.quote?.total_eur ?? 72.6).toFixed(2)}€</span>
                  </div>
                </div>

                <div>
                  <Label className="text-xs mb-1 block flex items-center gap-1">
                    <Ticket className="w-3.5 h-3.5" /> ¿Tienes un cupón?
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState(null); setCouponError(''); }}
                      placeholder="Código de cupón"
                      className="h-9 text-sm uppercase"
                      data-testid="additional-book-coupon-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={applyCoupon}
                      disabled={couponState === 'checking' || !couponCode.trim()}
                      data-testid="additional-book-coupon-apply"
                    >
                      {couponState === 'checking' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Aplicar'}
                    </Button>
                  </div>
                  {couponState === 'valid' && (
                    <p className="text-[11px] text-emerald-700 mt-1" data-testid="coupon-valid-msg">Cupón válido: descuento aplicado al total.</p>
                  )}
                  {couponState === 'invalid' && (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="coupon-invalid-msg">{couponError}</p>
                  )}
                </div>

                <Button
                  onClick={handlePurchaseCredit}
                  disabled={payingCredit}
                  className="w-full btn-primary gap-2"
                  data-testid="purchase-credit-btn"
                >
                  {payingCredit ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Procesando…
                    </>
                  ) : (purchaseInfo?.quote?.total_eur ?? 72.6) > 0 ? (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Pagar {Number(purchaseInfo?.quote?.total_eur ?? 72.6).toFixed(2)}€ y crear mi libro
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Crear mi libro gratis con el cupón
                    </>
                  )}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Pago seguro con Stripe. Recibirás tu factura por email al instante.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/dashboard')}
                  data-testid="payment-screen-back-btn"
                >
                  Volver a mi panel
                </Button>
              </CardContent>
            </Card>
          ) : (
          <>
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">
                {t('createBook.step')} {step} {t('createBook.of')} {totalSteps}
              </span>
              <span className="text-sm font-medium text-primary">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Step 1: Profile Selection */}
          {step === 1 && (
            <Card className="card-elevated" data-testid="step-1">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{t('createBook.whoIsBookFor')}</CardTitle>
                <CardDescription>{t('createBook.selectProfile')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {profiles.map((profile) => {
                    const Icon = profile.icon;
                    const isSelected = formData.profile_type === profile.value;
                    const isDisabled = profile.disabled;
                    
                    return (
                      <button
                        key={profile.value}
                        onClick={() => !isDisabled && updateForm('profile_type', profile.value)}
                        disabled={isDisabled}
                        className={`relative p-6 rounded-xl border-2 transition-all ${
                          isDisabled
                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                            : isSelected 
                              ? `${profile.color} border-current` 
                              : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`profile-select-${profile.value}`}
                      >
                        <Icon className={`w-10 h-10 mx-auto mb-3 ${isDisabled ? 'text-gray-300' : isSelected ? '' : 'text-gray-400'}`} />
                        <span className={`font-medium block ${isDisabled ? 'text-gray-400' : isSelected ? '' : 'text-gray-600'}`}>
                          {t(profile.label)}
                        </span>
                        {isDisabled && (
                          <span className="block text-[10px] text-gray-400 mt-1">No disponible</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Book Details */}
          {step === 2 && (
            <Card className="card-elevated" data-testid="step-2">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{t('createBook.bookDetails')}</CardTitle>
                <CardDescription>{t('createBook.tellUsMore')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="input-label">{t('book.recipientName')}</Label>
                    <Input
                      value={formData.recipient_name}
                      onChange={(e) => updateForm('recipient_name', e.target.value)}
                      placeholder="Ejem: María"
                      className="input-field"
                      data-testid="recipient-name-input"
                    />
                  </div>
                  <div>
                    <Label className="input-label">{t('auth.lastName')}</Label>
                    <Input
                      value={formData.recipient_last_name}
                      onChange={(e) => updateForm('recipient_last_name', e.target.value)}
                      placeholder="Ejem: García"
                      className="input-field"
                      data-testid="recipient-lastname-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="input-label">{t('book.recipientAge')}</Label>
                  <Input
                    type="number"
                    value={formData.recipient_age}
                    onChange={(e) => updateForm('recipient_age', e.target.value)}
                    placeholder="Ejem: 35"
                    className="input-field w-32"
                    data-testid="recipient-age-input"
                  />
                </div>

                <div>
                  <Label className="input-label">
                    <Gift className="w-4 h-4 inline mr-2" />
                    {t('book.occasion')}
                  </Label>
                  <Select value={formData.occasion} onValueChange={(v) => updateForm('occasion', v)}>
                    <SelectTrigger className="input-field" data-testid="occasion-select">
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {occasions.map((occ) => (
                        <SelectItem key={occ.value} value={occ.value}>{t(`occasions.${occ.key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.occasion === 'otro' && (
                  <div>
                    <Label className="input-label">{t('createBook.otherOccasion')}</Label>
                    <Input
                      value={formData.occasion_other}
                      onChange={(e) => updateForm('occasion_other', e.target.value)}
                      placeholder={t('createBook.otherOccasionPlaceholder')}
                      className="input-field"
                      data-testid="occasion-other-input"
                    />
                  </div>
                )}

                {formData.occasion && (
                  <div>
                    <Label className="input-label">Cuentanos mas sobre el motivo</Label>
                    <textarea
                      value={formData.occasion_detail}
                      onChange={(e) => updateForm('occasion_detail', e.target.value)}
                      placeholder="Ejem: Cumple 60 años y queremos sorprenderle con un libro que recoja los mejores momentos de su vida..."
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px]"
                      data-testid="occasion-detail-input"
                    />
                  </div>
                )}

                {/* Client relationship with protagonist */}
                <div>
                  <Label className="input-label">Tu relacion con {formData.recipient_name || 'el protagonista'}</Label>
                  <Select value={formData.client_relationship} onValueChange={(v) => updateForm('client_relationship', v)}>
                    <SelectTrigger className="input-field" data-testid="client-relationship-select">
                      <SelectValue placeholder="Selecciona tu relacion..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pareja">Pareja</SelectItem>
                      <SelectItem value="esposa">Esposa</SelectItem>
                      <SelectItem value="esposo">Esposo</SelectItem>
                      <SelectItem value="hija">Hija</SelectItem>
                      <SelectItem value="hijo">Hijo</SelectItem>
                      <SelectItem value="madre">Madre</SelectItem>
                      <SelectItem value="padre">Padre</SelectItem>
                      <SelectItem value="hermana">Hermana</SelectItem>
                      <SelectItem value="hermano">Hermano</SelectItem>
                      <SelectItem value="nieta">Nieta</SelectItem>
                      <SelectItem value="nieto">Nieto</SelectItem>
                      <SelectItem value="nuera">Nuera</SelectItem>
                      <SelectItem value="yerno">Yerno</SelectItem>
                      <SelectItem value="amiga">Amiga</SelectItem>
                      <SelectItem value="amigo">Amigo</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.client_relationship === 'otro' && (
                    <div className="mt-2">
                      <Input
                        value={formData.client_relationship_other || ''}
                        onChange={(e) => updateForm('client_relationship_other', e.target.value)}
                        placeholder="Indica tu relación (ej.: tía abuela, compañero de trabajo, mentor, ahijada…)"
                        className="input-field"
                        data-testid="client-relationship-other"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Esto nos ayuda a personalizar los emails que enviamos a los colaboradores.
                      </p>
                    </div>
                  )}
                </div>

                {/* Protagonist photo */}
                <div>
                  <Label className="input-label">
                    Foto del protagonista <span className="text-xs text-amber-600 font-normal">(recomendada)</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Se incluirá en las invitaciones que reciban tus colaboradores para que reconozcan al protagonista de un vistazo.
                  </p>
                  {formData.protagonist_photo ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={formData.protagonist_photo.url}
                        alt="Foto del protagonista"
                        className="w-24 h-24 rounded-xl object-cover border"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateForm('protagonist_photo', null)}
                        data-testid="remove-protagonist-photo"
                      >
                        Cambiar foto
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-primary cursor-pointer transition-colors bg-muted/20">
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                      ) : (
                        <>
                          <Camera className="w-6 h-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground">Haz clic para subir una foto</span>
                          <span className="text-[10px] text-muted-foreground mt-0.5">JPG, PNG o WebP · Max 5MB</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        disabled={uploadingPhoto}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 5 * 1024 * 1024) { toast.error('La imagen no puede superar 5MB'); return; }
                          setUploadingPhoto(true);
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            fd.append('book_id', 'new');
                            fd.append('context', 'protagonist');
                            const res = await axios.post(`${API_URL}/api/anecdote-photo/upload`, fd, {
                              withCredentials: true,
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });
                            updateForm('protagonist_photo', { url: `${API_URL}${res.data.photo_url}`, photo_id: res.data.photo_id, storage_path: res.data.storage_path });
                          } catch (error) {
                            // Surface the real backend message so the user
                            // knows what to fix (size, format, auth, etc).
                            const detail = error.response?.data?.detail;
                            toast.error(typeof detail === 'string' ? detail : 'Error al subir la foto');
                          }
                          finally { setUploadingPhoto(false); e.target.value = ''; }
                        }}
                        data-testid="protagonist-photo-input"
                      />
                    </label>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Language + Collaborator Purchase */}
          {step === 3 && (
            <Card className="card-elevated" data-testid="step-3">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{t('createBook.customization')}</CardTitle>
                <CardDescription>{t('createBook.chooseLanguageStyle')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="input-label">
                    <Globe className="w-4 h-4 inline mr-2" />
                    {t('book.language')}
                  </Label>
                  <Select value={formData.language} onValueChange={(v) => updateForm('language', v)}>
                    <SelectTrigger className="input-field" data-testid="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.value} value={lang.value}>
                          <span className="mr-2">{lang.flag}</span>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Collaborator Purchase Option */}
                <div className="border rounded-lg p-4 space-y-2" data-testid="collab-purchase-option">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.allow_collaborator_purchase}
                      onChange={(e) => updateForm('allow_collaborator_purchase', e.target.checked)}
                      className="w-4 h-4 rounded mt-0.5"
                      data-testid="allow-collab-create"
                    />
                    <div>
                      <Label className="text-sm font-medium cursor-pointer" onClick={() => updateForm('allow_collaborator_purchase', !formData.allow_collaborator_purchase)}>
                        <Users className="w-4 h-4 inline mr-1.5" />
                        Permitir a los colaboradores comprar el libro
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Los colaboradores podran pedir su propio ejemplar por 20€ + 5€ de envio. Podras cambiar esta opcion mas tarde.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Collaborator Referrals Option */}
                <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-4 space-y-2" data-testid="collab-referrals-option">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.allow_collaborator_referrals}
                      onChange={(e) => updateForm('allow_collaborator_referrals', e.target.checked)}
                      className="w-4 h-4 rounded mt-0.5"
                      data-testid="allow-collab-referrals-create"
                    />
                    <div>
                      <Label className="text-sm font-medium cursor-pointer text-orange-900"
                             onClick={() => updateForm('allow_collaborator_referrals', !formData.allow_collaborator_referrals)}>
                        <Users className="w-4 h-4 inline mr-1.5" />
                        Permitir que los colaboradores propongan otros colaboradores
                      </Label>
                      <p className="text-xs text-orange-800/80 mt-1">
                        Tras enviar su aportación, los colaboradores podrán proponer nuevos nombres. Las propuestas
                        aparecerán en tu panel con la etiqueta <strong>“Pendiente de invitar”</strong>; nada se enviará
                        hasta que tú las apruebes una a una.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="p-4 bg-muted/50 rounded-xl">
                  <h4 className="font-medium mb-2">{t('createBook.summary')}</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>{t('bookDetail.for')}:</strong> {formData.recipient_name} {formData.recipient_last_name}</p>
                    <p><strong>{t('book.profile')}:</strong> {t(`profiles.${formData.profile_type.replace('_', '')}`)}</p>
                    <p><strong>{t('book.occasion')}:</strong> {formData.occasion === 'otro' ? formData.occasion_other : t(`occasions.${formData.occasion}`)}</p>
                    <p><strong>{t('bookDetail.language')}:</strong> {languages.find(l => l.value === formData.language)?.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/dashboard')}
              className="gap-2"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              {step > 1 ? t('common.previous') : t('common.cancel')}
            </Button>

            {step < totalSteps ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="btn-primary gap-2"
                data-testid="next-btn"
              >
                {t('common.next')}
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                className="btn-primary gap-2 min-w-[180px]"
                data-testid="create-book-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creando libro…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Crear libro
                  </>
                )}
              </Button>
            )}
          </div>
          </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CreateBook;
